import {parse, format} from 'url';
import _ from 'lodash';
import {
    loadGetAppData,
    loadGetInitialData,
    loadGetInitialProps,
    getSlashRedirectPath,
    getPageFromPath,
    allowStaffOnly,
    allowOnlyLocale,
    getInternalPathname,
    getComponentName,
} from '../../common/utils/common';
import {routes} from '../../common/routes';
import {getURL} from '../utils';
import EventEmitter from '../EventEmitter';
import metrika from '../counters/metrika';
import pixel, {PixelEventType} from '../counters/pixel';
import {setRumPage} from 'services/rum';
import {
    getTld,
    getLocale,
    hasLocalePrefix,
    addLocalePrefix,
    removeLocalePrefix,
} from '../../common/i18n/utils';

const EXTERNAL_ROUTES = [
    /^\/promo\/(.*)$/,
    /^\/resources\/(.*)$/,
    /^\/promo-mdb(\/?.*)$/,
    /^\/events\/1-year(\/?.*)$/,
    /^\/events\/scale-2019(\/?.*)$/,
    /^\/events\/scale-2020(\/?.*)$/,
    /^\/services\/speechkit\/solutions(\/?.*)$/,
];

export default class Router {
    constructor(
        pathname,
        query,
        hash,
        as,
        hostname,
        constructablePages,
        {page, errorPage, initialProps, pageLoader, useSSR, context, internalPathname},
    ) {
        this.route = removeLocalePrefix(internalPathname, context.locale.code);

        this.useSSR = useSSR;
        this.pageLoader = pageLoader;
        this.errorPage = errorPage;
        this.pathname = pathname;
        this.query = query;
        this.hash = hash;
        this.as = as;
        this.hostname = hostname;
        this.subscriptions = new Set();
        this.constructablePages = constructablePages;
        this.context = context;
        this.navigationHistory = [pathname];
        this.internalPathname = internalPathname;

        this.pages = {};
        this.currentPageData = null;

        if (page !== errorPage) {
            this.currentPageData = {page, props: initialProps};

            if (_.isEmpty(query)) {
                this.pages[this.route] = this.currentPageData;
            }
        }
        this.appDataCache = {};

        this.events = new EventEmitter();

        this.events.on('routeChangeStart', this.handleRouteChange.bind(this));
        this.changeState('replaceState', format({pathname, query}), getURL());
        window.addEventListener('popstate', this.handlePopState);
    }

    urlIsNew(pathname, query) {
        return this.pathname !== pathname || !_.isEqual(this.query, query);
    }

    isExternalRoute(pathname) {
        return EXTERNAL_ROUTES.some((routeRegex) => routeRegex.test(pathname));
    }

    isNewLocale(hostname, pathname) {
        return (
            hasLocalePrefix(pathname) &&
            getLocale(hostname, pathname).code !== this.context.locale.code
        );
    }

    handleRouteChange(event) {
        switch (event.originMethod) {
            case 'pushState':
                this.navigationHistory.push(event.as);
                break;
            case 'popState':
                if (this.navigationHistory.length > 0) {
                    this.navigationHistory.length = this.navigationHistory.length - 1;
                }
                break;
            case 'replaceState':
                this.navigationHistory.pop();
                this.navigationHistory = [...this.navigationHistory, event.as];
                break;
        }
    }

    handlePopState = (event) => {
        if (!event.state) {
            const {pathname, query} = this;
            this.changeState('replaceState', format({pathname, query}), getURL());
            return;
        }

        const {url, as, options} = event.state;
        this.replace(url, as, options);
    };

    back() {
        window.history.back();
    }

    push(url, as = url, options = {}) {
        return this.change('pushState', url, as, options);
    }

    replace(url, as = url, options = {}) {
        return this.change('replaceState', url, as, options);
    }

    changeState(method, url, as, options) {
        const tld = getTld(this.hostname);
        let newUrl = url;
        let newAs = as;

        if (tld !== this.context.locale.lang) {
            newUrl = addLocalePrefix(url, this.context.locale.code);
            newAs = addLocalePrefix(as, this.context.locale.code);
        }

        window.history[method]({url: newUrl, as: newAs, options}, null, newAs);
    }

    reload(method, url, as, options) {
        this.changeState(method, url, as, options);
        window.location.reload();
        // prevent automatic browser scroll on refresh: https://developer.mozilla.org/en-US/docs/Web/API/History/scrollRestoration
        history.scrollRestoration = 'manual';
        return false;
    }

    async change(originMethod, originUrl, originAs, options) {
        let method = originMethod;
        this.cancelComponentLoad();
        const parsedUrl = parse(originUrl, true);
        const hostname = parsedUrl.hostname || window.location.hostname;
        const pathname = parsedUrl.pathname || window.location.pathname;
        const query = parsedUrl.query;
        const page = getPageFromPath(pathname);
        const [, hash = ''] = (parsedUrl.hash || '').split('#');
        const url = format({pathname, query, hash});
        const as = getURL(originAs);

        setRumPage(page);

        if (this.isNewLocale(hostname, pathname) || this.isExternalRoute(pathname)) {
            return this.reload(method, url, as, options);
        }

        const internalPathname = getInternalPathname(pathname, this.internalPathname);
        const route = removeLocalePrefix(internalPathname, this.context.locale.code);

        if (this.onlyHashChange(as)) {
            this.events.emit('hashChangeStart');
            this.set(route, internalPathname, pathname, query, hash, as, this.pages[route]);
            this.changeState(method, url, as);
            this.scrollToHash(hash);
            this.events.emit('hashChangeComplete');

            return false;
        }

        if (!this.urlIsNew(pathname, query)) {
            method = 'replaceState';
        }

        this.events.emit('routeChangeStart', {originMethod, url, as, options});

        let routeInfo = null;

        if (this.pathname === pathname && options?.shallow && this.currentPageData) {
            routeInfo = this.currentPageData;
        } else {
            try {
                routeInfo = await this.getRouteInfo(route, pathname, query);
            } catch (ex) {
                const statusCode = ex.statusCode || (ex.response && ex.response.status);
                if (statusCode === 404 && !options.slashRedirected && route !== '/') {
                    // trying to fix path
                    const path = getSlashRedirectPath(pathname);
                    const newUrl = format({pathname: path, query, hash});
                    return this.change(method, newUrl, newUrl, {slashRedirected: true});
                }

                if (ex.cancelled) {
                    return false;
                }

                if (this.useSSR) {
                    return this.reload(method, url, as, options);
                }

                routeInfo = await this.getErrorRouteInfo(ex, page, pathname, query);
            }
        }

        this.set(route, internalPathname, pathname, query, hash, as, routeInfo);
        this.changeState(method, url, as, options);
        this.events.emit('routeChangeComplete');
        return true;
    }

    set(route, internalPathname, pathname, query, hash, as, data) {
        metrika.hit(as, {referer: this.as});
        pixel.trackStandard(PixelEventType.PageView);
        this.route = route;
        this.internalPathname = internalPathname;
        this.pathname = pathname;
        this.query = query;
        this.hash = hash;
        this.as = as;
        this.notify(data);
    }

    getParams() {
        let params;
        if (this.paramsChanged()) {
            params = {
                page: getPageFromPath(this.pathname),
                pathname: this.pathname,
                query: this.query,
                hash: this.hash,
                as: this.as,
                hostname: this.hostname,
                events: this.events,
            };
            this.prevParams = params;
        } else {
            params = this.prevParams;
        }

        return params;
    }

    paramsChanged() {
        if (this.prevParams) {
            return (
                this.internalPathname !== this.prevParams.internalPathname ||
                this.pathname !== this.prevParams.pathname ||
                this.query !== this.prevParams.query ||
                this.hash !== this.prevParams.hash ||
                this.as !== this.prevParams.as ||
                this.hostname !== this.prevParams.hostname
            );
        } else {
            return true;
        }
    }

    onlyHashChange(as) {
        const [oldUrlNoHash, oldHash] = this.as.split('#');
        const [newUrlNoHash, newHash] = as.split('#');

        if (oldUrlNoHash !== newUrlNoHash) {
            return false;
        }

        return oldHash !== newHash;
    }

    scrollToHash(hash) {
        if (hash === 'top') {
            window.scrollTo({top: 0, behavior: 'smooth'});
            return;
        }

        const el = document.getElementById(hash);
        const browserName = this.context.browser && this.context.browser.name;

        if (el) {
            el.scrollIntoView({behavior: browserName === 'Yandex' ? 'auto' : 'smooth'});
        }
    }

    async getRouteInfo(route, pathname, query) {
        let routeInfo, routeData;

        routeInfo = _.isEmpty(query) ? this.pages[route] : null;
        if (!routeInfo) {
            const ctx = {
                ...this.context,
                route,
                pathname,
                query,
                constructablePages: this.constructablePages,
            };

            routeData =
                routes.find(
                    ({regex, hasMatch, staffOnly, langs, regions}) =>
                        ((regex && regex.test(route)) || (hasMatch && hasMatch(ctx))) &&
                        (!staffOnly || allowStaffOnly(this.context.user)) &&
                        ((!langs && !regions) ||
                            allowOnlyLocale(this.context.locale, {langs, regions})),
                ) || {};

            const {pageId = route, getParams, hasParams, regex} = routeData;

            routeInfo = {page: await this.fetchPage(pageId)};

            const routeParams = hasParams
                ? (getParams && getParams(ctx)) || (regex && route.match(regex)[1])
                : query;

            const [appData, componentData] = await Promise.all([
                typeof routeData.appData === 'object'
                    ? routeData.appData
                    : await this.getAppData(routeInfo.page),
                this.getInitialData(routeInfo.page, routeParams),
            ]);

            ctx.appData = appData;
            ctx.componentData = componentData;
            routeInfo.props = await this.getInitialProps(routeInfo.page, ctx);

            if (_.isEmpty(query)) {
                this.pages[route] = routeInfo;
            }
        }

        this.currentPageData = routeInfo;

        return routeInfo;
    }

    async getErrorRouteInfo(error, page, pathname, query) {
        const routeInfo = {
            page: this.errorPage,
            error,
        };
        const ctx = {
            ...this.context,
            err: error,
            errorType: 'client_router',
            page,
            pathname,
            query,
        };
        routeInfo.props = await this.getInitialProps(routeInfo.page, ctx);
        return routeInfo;
    }

    async loadCancelable(fn, errorMessage, ...args) {
        let cancelled = false;
        const cancel = (this.pageLoadCancel = () => {
            cancelled = true;
        });

        const data = await fn.call(this, ...args);

        if (cancelled) {
            const error = new Error(errorMessage);
            error.cancelled = true;
            throw error;
        }

        if (cancel === this.pageLoadCancel) {
            this.pageLoadCancel = null;
        }

        return data;
    }

    fetchPage = (route) =>
        this.loadCancelable(
            this.fetchRoute,
            `Component fetching is cancelled for route: "${route}"`,
            route,
        );
    getAppData = async ({Component}, params) => {
        const componentName = getComponentName(Component);
        if (!params && this.appDataCache[componentName]) {
            return this.appDataCache[componentName];
        }

        const addData = await this.loadCancelable(
            loadGetAppData,
            `Add data loading is cancelled for "${Component.name}" component`,
            Component,
            params,
        );

        if (!params) {
            this.appDataCache[componentName] = addData;
        }
        return addData;
    };
    getInitialData = async ({Component}, params) =>
        this.loadCancelable(
            loadGetInitialData,
            `Initial data loading is cancelled for "${Component.name}" component`,
            Component,
            this.context.locale,
            params,
        );
    getInitialProps = async ({App, Component}, ctx) =>
        this.loadCancelable(
            loadGetInitialProps,
            `Initial props loading is cancelled for "${Component.name}" component`,
            App,
            {Component, ctx},
        );

    cancelComponentLoad() {
        if (this.pageLoadCancel) {
            this.pageLoadCancel();
            this.events.emit('routeChangeError', new Error('Route cancelled'));
            this.pageLoadCancel = null;
        }
    }

    fetchRoute(route) {
        return this.pageLoader.loadPage(route);
    }

    notify(data) {
        this.subscriptions.forEach((fn) => fn(data));
    }

    subscribe(fn) {
        this.subscriptions.add(fn);
        return () => this.subscriptions.delete(fn);
    }
}
